import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Typography,
  Paper,
  Chip,
  Alert,
  Stack,
  Box,
  useMediaQuery,
  useTheme,
  TextField,
  InputAdornment,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Popover,
  Drawer,
  Divider,
  Badge,
  Tooltip,
  Collapse,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import AddIcon from '@mui/icons-material/Add';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import ViewAgendaOutlinedIcon from '@mui/icons-material/ViewAgendaOutlined';
import ViewModuleOutlinedIcon from '@mui/icons-material/ViewModuleOutlined';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import PhoneInTalkOutlinedIcon from '@mui/icons-material/PhoneInTalkOutlined';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { useSearchParams } from 'react-router-dom';
import {
  collection,
  getFirestore,
  onSnapshot,
  orderBy,
  query,
  Timestamp,
  where,
  getDoc,
  doc,
  getDocs,
  limit,
  getCountFromServer,
} from 'firebase/firestore';
import { useAuth, usePermissions } from '../AuthContext';
import AddLeadDrawer from '../components/AddLeadDrawer';
import LeadDetailDrawer from '../components/LeadDetailDrawer';
import LeadCompactCard, { AppointmentBadge } from '../components/LeadCompactCard';
import { useMyWhatsAppUnread } from '../components/WhatsAppNotificationPanel';
import { isCareerApplicationSource } from '../utils/careerApplications';

type Lead = {
  id: string;
  name: string;
  status: string;
  assignedTo?: string;
  assignedToName?: string | null;
  source?: string;
  phone?: string;
  email?: string;
  nextFollowUpDate?: Timestamp;
  notes?: string | string[];
  rating?: number | null;
  activityCount?: number | null;
  lastActivityAt?: Timestamp | null;
  createdAt?: Timestamp;
  appointment?: {
    type?: 'site-visit' | 'office-meeting';
    scheduledAt?: Timestamp;
    status?: 'scheduled' | 'completed' | 'cancelled' | 'no-show';
  } | null;
};

type UserDoc = {
  id: string;
  email?: string;
  name?: string;
  roleId?: string;
  role?: string;
};

export default function FollowUpPage() {
  const db = useMemo(() => getFirestore(), []);
  const { canAction } = usePermissions();
  const { currentUser, roleId, userRole } = useAuth();
  const waUnreadByPhone = useMyWhatsAppUnread();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  // "Desktop only" for the AppSheet-style tree filter.
  // Use both width and a fine pointer so large tablets (touch) keep the pill UI.
  const isPointerFine = useMediaQuery('(pointer: fine)');
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg')) && isPointerFine;
  const [leads, setLeads] = useState<Lead[]>([]);
  const [noFollowUpLeads, setNoFollowUpLeads] = useState<Lead[]>([]);
  const [users, setUsers] = useState<UserDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [detailDrawerOpen, setDetailDrawerOpen] = useState(false);
  const [detailLeadId, setDetailLeadId] = useState<string | null>(null);
  const [detailLeadFallback, setDetailLeadFallback] = useState<Lead | null>(null);
  const [detailWidth, setDetailWidth] = useState(460);
  const [statuses, setStatuses] = useState<any[]>([]);
  const [searchParams, setSearchParams] = useSearchParams();

  const [searchRaw, setSearchRaw] = useState('');
  const [search, setSearch] = useState('');
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [phoneLookupOpen, setPhoneLookupOpen] = useState(false);
  const [phoneLookupRaw, setPhoneLookupRaw] = useState('');
  const [phoneLookupError, setPhoneLookupError] = useState<string | null>(null);
  const [phoneLookupBusy, setPhoneLookupBusy] = useState(false);
  const [siteVisitCountByLead, setSiteVisitCountByLead] = useState<Record<string, number>>({});

  const [selectedAssigneeKey, setSelectedAssigneeKey] = useState<string | null>(null); // null => All
  const [selectedBucket, setSelectedBucket] = useState<'today' | 'past' | 'no-followup' | null>(null);
  const [selectedStatusKey, setSelectedStatusKey] = useState<string | null>(null);
  const [selectedTopQueue, setSelectedTopQueue] = useState<'wa-unread' | null>(null);
  const [mobileDefaultAssigneeReady, setMobileDefaultAssigneeReady] = useState(false);
  const [ratingFilter, setRatingFilter] = useState<number | 'unrated' | null>(null); // null => any
  type QuickFilterKey =
    | 'no-activity'
    | 'no-followup'
    | 'scheduled-today'
    | 'scheduled-upcoming'
    | 'overdue-followup'
    | 'unassigned'
    | 'high-rating';
  const [quickFilter, setQuickFilter] = useState<QuickFilterKey | null>(null);
  const [mobileInlineOpen, setMobileInlineOpen] = useState(true);
  const [extraFiltersOpenMobile, setExtraFiltersOpenMobile] = useState(false);
  const [extraFiltersAnchorEl, setExtraFiltersAnchorEl] = useState<HTMLElement | null>(null);
  const [filterUiMode, setFilterUiMode] = useState<'pills' | 'tree'>(() => {
    try {
      const v = localStorage.getItem('followUpFilterUiMode');
      return v === 'tree' ? 'tree' : 'pills';
    } catch {
      return 'pills';
    }
  });
  const [treePanelWidth, setTreePanelWidth] = useState<number>(() => {
    try {
      const raw = localStorage.getItem('followUpTreePanelWidth');
      const n = raw ? Number(raw) : NaN;
      return Number.isFinite(n) ? n : 280;
    } catch {
      return 280;
    }
  });
  const treeResizeRef = useRef<{ startX: number; startW: number } | null>(null);
  const [treeResizing, setTreeResizing] = useState(false);
  const [treeOpenAssigneeKey, setTreeOpenAssigneeKey] = useState<string | null>(null);
  const [treeOpenBucket, setTreeOpenBucket] = useState<'today' | 'past' | 'no-followup' | null>(null);
  const isAdmin = useMemo(() => {
    const r = String(roleId || userRole || '').toLowerCase();
    return r === 'admin' || r === 'super-admin' || r === 'superadmin';
  }, [roleId, userRole]);

  useEffect(() => {
    // Force pills on tablet/mobile, but keep the user's preference saved for desktop.
    if (!isDesktop && filterUiMode === 'tree') setFilterUiMode('pills');
  }, [filterUiMode, isDesktop]);

  useEffect(() => {
    try {
      localStorage.setItem('followUpFilterUiMode', filterUiMode);
    } catch {
      // ignore
    }
  }, [filterUiMode]);

  useEffect(() => {
    try {
      localStorage.setItem('followUpTreePanelWidth', String(treePanelWidth));
    } catch {
      // ignore
    }
  }, [treePanelWidth]);

  useEffect(() => {
    if (!treeResizing) return;
    const onMove = (e: MouseEvent) => {
      if (!treeResizeRef.current) return;
      const dx = e.clientX - treeResizeRef.current.startX;
      const next = treeResizeRef.current.startW + dx;
      setTreePanelWidth(Math.max(220, Math.min(420, next)));
    };
    const onUp = () => {
      setTreeResizing(false);
      treeResizeRef.current = null;
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [treeResizing]);

  // Default to list view for everyone; only admins can toggle and persist a preference.
  const [leadListView, setLeadListView] = useState<'card' | 'sheet'>('sheet');
  const didAutoSelectMobileAssignee = useRef(false);

  const normalize = useCallback((v: string) => v.trim().toLowerCase(), []);

  useEffect(() => {
    if (!isAdmin) {
      setLeadListView('sheet');
      return;
    }
    try {
      const v = localStorage.getItem('leadListView');
      setLeadListView(v === 'card' ? 'card' : 'sheet');
    } catch {
      // ignore
    }
  }, [isAdmin]);

  useEffect(() => {
    if (!isAdmin) return;
    try {
      localStorage.setItem('leadListView', leadListView);
    } catch {
      // ignore
    }
  }, [isAdmin, leadListView]);

  const canSeeOthers = useMemo(() => {
    const r = String(roleId || userRole || '').toLowerCase();
    return r === 'super-admin' || r === 'admin' || r === 'sales-manager';
  }, [roleId, userRole]);

  const findStatusDoc = useCallback(
    (val?: string | null) => {
      if (!val) return null;
      const byId = statuses.find((s) => s.id === val);
      if (byId) return byId;
      const norm = normalize(val);
      const byLabel = statuses.find((s) => typeof s.label === 'string' && normalize(s.label) === norm);
      if (byLabel) return byLabel;
      if (norm === 'new') {
        const starts = statuses.find((s) => typeof s.label === 'string' && normalize(s.label).startsWith('new'));
        if (starts) return starts;
      }
      return null;
    },
    [statuses, normalize]
  );

  const statusColor = useCallback(
    (statusId?: string | null): string | null => {
      if (!statusId) return null;
      const byId = statuses.find((x) => x.id === statusId);
      const norm = statusId.trim().toLowerCase();
      const byLabel = statuses.find(
        (x: any) => typeof x?.label === 'string' && String(x.label).trim().toLowerCase() === norm
      );
      const byStartsWithNew =
        norm === 'new'
          ? statuses.find((x: any) => typeof x?.label === 'string' && String(x.label).trim().toLowerCase().startsWith('new'))
          : null;
      const s = byId || byLabel || byStartsWithNew;
      const c = typeof (s as any)?.color === 'string' ? String((s as any).color).trim() : '';
      return /^#([0-9a-fA-F]{6})$/.test(c) ? c : null;
    },
    [statuses]
  );

  // Fetch only due follow-ups (today or past).
  useEffect(() => {
    // Use end-of-day so we include "today" follow-ups even if the stored Timestamp
    // isn't exactly at local midnight (timezone parsing differences).
    const endOfToday = new Date();
    endOfToday.setHours(23, 59, 59, 999);
    const leadsRef = collection(db, 'leads');
    const qDue = query(
      leadsRef,
      where('nextFollowUpDate', '<=', Timestamp.fromDate(endOfToday)),
      orderBy('nextFollowUpDate', 'asc'),
      limit(1000)
    );
    const qNoFollowUp = query(
      leadsRef,
      where('nextFollowUpDate', '==', null),
      limit(500)
    );
    let dueRows: Lead[] = [];

    const recompute = () => {
      const data = dueRows.slice().sort((a, b) => {
        const da = a.nextFollowUpDate?.toDate ?
          a.nextFollowUpDate.toDate().getTime() :
          0;
        const db = b.nextFollowUpDate?.toDate ?
          b.nextFollowUpDate.toDate().getTime() :
          0;
        return da - db;
      });
      setLeads(data);
      setLoading(false);
    };

    const unsubDue = onSnapshot(
      qDue,
      (snapshot) => {
        dueRows = snapshot.docs.map(
          (doc) =>
            ({
              id: doc.id,
              ...doc.data(),
            } as Lead)
        ).filter((row: any) => (
          !isCareerApplicationSource(row.source) &&
          !row.isMerged &&
          !row.archived
        ));
        recompute();
      },
      (err) => {
        console.error('Error fetching follow-ups:', err);
        setError(`Failed to fetch follow-ups. ${err?.message || ''}`.trim());
        setLoading(false);
      }
    );
    const unsubNoFollowUp = onSnapshot(
      qNoFollowUp,
      (snapshot) => {
        const rows = snapshot.docs
          .map(
            (doc) =>
              ({
                id: doc.id,
                ...doc.data(),
              } as Lead)
          )
          .filter((row: any) => {
            if (isCareerApplicationSource(row?.source)) return false;
            if (row?.isMerged || row?.archived) return false;
            const value = row?.nextFollowUpDate;
            return !(value && typeof value?.toDate === 'function');
          });
        setNoFollowUpLeads(rows);
      },
      (err) => {
        console.error('Error fetching no follow-up leads:', err);
        setError(`Failed to fetch follow-ups. ${err?.message || ''}`.trim());
        setLoading(false);
      }
    );

    return () => {
      unsubDue();
      unsubNoFollowUp();
    };
  }, [db]);

  // users for assignee grouping + role filtering
  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, 'users'),
      (snap) => {
        setUsers(snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) } as UserDoc)));
      },
      () => setUsers([])
    );
    return () => unsub();
  }, [db]);

  // statuses for labels + ordering
  useEffect(() => {
    const q = query(collection(db, 'leadStatuses'), orderBy('order', 'asc'));
    const unsub = onSnapshot(
      q,
      (snap) => setStatuses(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      () => setStatuses([])
    );
    return () => unsub();
  }, [db]);

  const startOfToday = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const endOfToday = useMemo(() => {
    const d = new Date();
    d.setHours(23, 59, 59, 999);
    return d;
  }, []);

  const viewerRole = useMemo(() => String(roleId || userRole || '').toLowerCase(), [roleId, userRole]);
  const isAdminViewer = useMemo(() => {
    return viewerRole === 'super-admin' || viewerRole === 'superadmin' || viewerRole === 'admin';
  }, [viewerRole]);
  const isManagerViewer = useMemo(() => {
    return viewerRole === 'sales-manager' || viewerRole === 'salesmanager';
  }, [viewerRole]);
  const isSalesViewer = useMemo(() => {
    return (
      viewerRole === 'sales-team' ||
      viewerRole === 'salesteam' ||
      viewerRole === 'trainee'
    );
  }, [viewerRole]);

  const userByEmail = useMemo(() => {
    const map = new Map<string, UserDoc>();
    users.forEach((u) => {
      if (u.email) map.set(u.email, u);
    });
    return map;
  }, [users]);
  const effectiveAssigneeEmail = useCallback(
    (lead: Lead): string | null => {
      const raw = String(lead?.assignedTo || '').trim();
      return raw || null;
    },
    []
  );

  const assigneeLabel = useCallback(
    (email: string | null) => {
      if (!email) return 'Unassigned';
      const u = userByEmail.get(email);
      if (u?.name) return u.name;
      const local = email.includes('@') ? email.split('@')[0] : email;
      return local || email;
    },
    [userByEmail]
  );

  const visibleAssigneeEmails = useMemo(() => {
    const me = currentUser?.email || null;
    if (!me) return { mode: 'none' as const, set: new Set<string>() };
    if (isAdminViewer) return { mode: 'all' as const, set: new Set<string>() };
    if (isManagerViewer) {
      const s = new Set<string>();
      s.add(me);
      users.forEach((u) => {
        const r = String(u.roleId || u.role || '').toLowerCase();
        if ((r === 'sales-team' || r === 'salesteam' || r === 'trainee') && u.email) s.add(u.email);
      });
      return { mode: 'set' as const, set: s };
    }
    if (isSalesViewer) return { mode: 'set' as const, set: new Set([me]) };
    return { mode: 'set' as const, set: new Set([me]) };
  }, [currentUser?.email, isAdminViewer, isManagerViewer, isSalesViewer, users]);

  const visibleLeads = useMemo(() => {
    if (visibleAssigneeEmails.mode === 'all') return leads;
    const allowed = visibleAssigneeEmails.set;
    return leads.filter((l) => {
      const a = effectiveAssigneeEmail(l);
      if (!a) return isAdminViewer || isManagerViewer;
      return allowed.has(a);
    });
  }, [effectiveAssigneeEmail, isAdminViewer, isManagerViewer, leads, visibleAssigneeEmails]);

  const baseScopedLeads = visibleLeads;
  const visibleNoFollowUpLeads = useMemo(() => {
    if (visibleAssigneeEmails.mode === 'all') return noFollowUpLeads;
    const allowed = visibleAssigneeEmails.set;
    return noFollowUpLeads.filter((l) => {
      const a = effectiveAssigneeEmail(l);
      if (!a) return isAdminViewer || isManagerViewer;
      return allowed.has(a);
    });
  }, [effectiveAssigneeEmail, isAdminViewer, isManagerViewer, noFollowUpLeads, visibleAssigneeEmails]);
  const isClosedLikeStatus = useCallback(
    (statusRaw?: string | null): boolean => {
      const raw = String(statusRaw || '').trim();
      if (!raw) return false;
      const n = normalize(raw);
      if (n === 'closed' || n === 'completed') return true;
      const byId = statuses.find((s: any) => String(s?.id || '') === raw);
      const byLabel = statuses.find((s: any) => normalize(String(s?.label || '')) === n);
      const labelNorm = normalize(String((byId || byLabel)?.label || ''));
      const idNorm = normalize(String((byId || byLabel)?.id || ''));
      return (
        labelNorm === 'closed' ||
        idNorm === 'closed' ||
        labelNorm === 'completed' ||
        idNorm === 'completed'
      );
    },
    [normalize, statuses]
  );

  const getBucket = useCallback(
    (l: Lead): 'today' | 'past' | null => {
      const dt: Date | null = l.nextFollowUpDate?.toDate ? l.nextFollowUpDate.toDate() : null;
      if (!dt) return null;
      return dt < startOfToday ? 'past' : 'today';
    },
    [startOfToday]
  );

  const apptIndicator = useCallback((l: Lead): AppointmentBadge => {
    const appt = l.appointment;
    if (!appt || appt.status !== 'scheduled' || !appt.scheduledAt?.toDate) return null;
    const dt = appt.scheduledAt.toDate();
    const now = new Date();
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    const end = new Date();
    end.setHours(23, 59, 59, 999);
    const isToday = dt >= start && dt <= end;
    const isPast = dt < now;
    const time = dt.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true });
    const date = dt.toLocaleDateString();
    // On mobile, keep the pill compact (omit meeting type prefix).
    const kind = isMobile ? '' : appt.type === 'office-meeting' ? 'Office' : '';
    const label = isToday ? `Today ${time}` : `${date} ${time}`;
    const full = kind ? `${kind} • ${label}` : label;
    const color = (isToday && !isPast ? 'success' : isPast ? 'warning' : 'default') as
      | 'default'
      | 'success'
      | 'warning';
    return { label: full, color };
  }, [isMobile]);

  type StatusAgg = { key: string; label: string; count: number; order: number };
  const computeAgg = useCallback(
    (items: Lead[]): { total: number; rows: StatusAgg[] } => {
      const map = new Map<string, StatusAgg>();
      for (const l of items) {
        const doc = findStatusDoc(l.status);
        const key = doc?.id || normalize(doc?.label || l.status);
        const label = doc?.label || l.status;
        const order = typeof doc?.order === 'number' ? doc.order : 9999;
        const curr = map.get(key);
        if (curr) curr.count += 1;
        else map.set(key, { key, label, count: 1, order });
      }
      const rows = Array.from(map.values()).sort((a, b) => a.order - b.order || a.label.localeCompare(b.label));
      return { total: items.length, rows };
    },
    [findStatusDoc, normalize]
  );

  type BucketAgg = { total: number; rows: StatusAgg[] };
  type AssigneeAgg = {
    key: string; // email or "__unassigned__"
    label: string;
    total: number;
    today: BucketAgg;
    past: BucketAgg;
    noFollowUp: BucketAgg;
  };

  const quickScopedLeads = useMemo(() => {
    if (!quickFilter) return baseScopedLeads;

    if (quickFilter === 'no-activity') {
      // Only treat leads as "no activity" when we *explicitly* know the count is 0.
      // (Older imported leads may not have activityCount populated yet.)
      return baseScopedLeads.filter((l) => l.activityCount === 0);
    }
    if (quickFilter === 'no-followup') {
      return visibleNoFollowUpLeads.filter((l) => !isClosedLikeStatus(l.status));
    }

    if (quickFilter === 'unassigned') {
      return baseScopedLeads.filter((l) => !effectiveAssigneeEmail(l));
    }

    if (quickFilter === 'high-rating') {
      return baseScopedLeads.filter((l) => Number(l.rating || 0) >= 4);
    }

    if (quickFilter === 'overdue-followup') {
      return baseScopedLeads.filter((l) => {
        if (isClosedLikeStatus(l.status)) return false;
        const dt: Date | null = l.nextFollowUpDate?.toDate ? l.nextFollowUpDate.toDate() : null;
        if (!dt) return false; // no-follow-up open leads are treated as "today", not overdue
        return dt < startOfToday;
      });
    }

    // appointment-based quick filters
    return baseScopedLeads.filter((l) => {
      const appt = l.appointment;
      if (!appt || appt.status !== 'scheduled' || !appt.scheduledAt?.toDate) return false;
      const dt = appt.scheduledAt.toDate();
      if (quickFilter === 'scheduled-today') return dt >= startOfToday && dt <= endOfToday;
      if (quickFilter === 'scheduled-upcoming') return dt > endOfToday;
      return true;
    });
  }, [baseScopedLeads, effectiveAssigneeEmail, endOfToday, isClosedLikeStatus, quickFilter, startOfToday, visibleNoFollowUpLeads]);

  const ratingScopedLeads = useMemo(() => {
    if (ratingFilter === null) return quickScopedLeads;
    if (ratingFilter === 'unrated') {
      return quickScopedLeads.filter((l) => !l.rating || Number(l.rating) <= 0);
    }
    return quickScopedLeads.filter((l) => Number(l.rating || 0) === ratingFilter);
  }, [quickScopedLeads, ratingFilter]);
  const noFollowUpScopedLeads = useMemo(() => {
    const openRows = visibleNoFollowUpLeads.filter((l) => !isClosedLikeStatus(l.status));
    const ratedRows =
      ratingFilter === null ?
        openRows :
        ratingFilter === 'unrated' ?
          openRows.filter((l) => !l.rating || Number(l.rating) <= 0) :
          openRows.filter((l) => Number(l.rating || 0) === ratingFilter);
    if (quickFilter === null) return ratedRows;
    return [];
  }, [isClosedLikeStatus, quickFilter, ratingFilter, visibleNoFollowUpLeads]);

  const allTreeScopedLeads = useMemo(() => {
    const map = new Map<string, Lead>();
    [...ratingScopedLeads, ...noFollowUpScopedLeads].forEach((lead) => {
      map.set(String(lead.id), lead);
    });
    return Array.from(map.values());
  }, [noFollowUpScopedLeads, ratingScopedLeads]);

  const whatsappUnreadLeadIds = useMemo(() => {
    const ids = new Set<string>();
    allTreeScopedLeads.forEach((lead: any) => {
      const phone10 = String(lead?.phone10 || lead?.phone || '').replace(/\D/g, '').slice(-10);
      if (phone10 && (waUnreadByPhone.get(phone10) || 0) > 0) {
        ids.add(String(lead.id));
      }
    });
    return ids;
  }, [allTreeScopedLeads, waUnreadByPhone]);

  const whatsappUnreadCount = whatsappUnreadLeadIds.size;

  const treeAgg = useMemo(() => {
    const groups = new Map<string, { label: string; leads: Lead[] }>();
    const ensure = (key: string, label: string) => {
      const ex = groups.get(key);
      if (ex) return ex;
      const created = { label, leads: [] as Lead[] };
      groups.set(key, created);
      return created;
    };
    ratingScopedLeads.forEach((l) => {
      const email = effectiveAssigneeEmail(l);
      const key = email || '__unassigned__';
      const label = assigneeLabel(email);
      ensure(key, label).leads.push(l);
    });
    noFollowUpScopedLeads.forEach((l) => {
      const email = effectiveAssigneeEmail(l);
      const key = email || '__unassigned__';
      const label = assigneeLabel(email);
      ensure(key, label).leads.push(l);
    });

    const assignees: AssigneeAgg[] = Array.from(groups.entries()).map(([key, g]) => {
      const today = g.leads.filter((l) => getBucket(l) === 'today');
      const past = g.leads.filter((l) => getBucket(l) === 'past');
      const noFollowUp = g.leads.filter((l) => !l.nextFollowUpDate);
      return {
        key,
        label: g.label,
        total: g.leads.length,
        today: computeAgg(today),
        past: computeAgg(past),
        noFollowUp: computeAgg(noFollowUp),
      };
    });

    // Sort: current user first (if present), then others alphabetically, unassigned last.
    const me = currentUser?.email || '';
    assignees.sort((a, b) => {
      if (a.key === '__unassigned__') return 1;
      if (b.key === '__unassigned__') return -1;
      if (a.key === me) return -1;
      if (b.key === me) return 1;
      return a.label.localeCompare(b.label);
    });

    return {
      total: ratingScopedLeads.length + noFollowUpScopedLeads.length,
      assignees,
    };
  }, [
    assigneeLabel,
    computeAgg,
    currentUser?.email,
    effectiveAssigneeEmail,
    getBucket,
    noFollowUpScopedLeads,
    ratingScopedLeads,
  ]);

  const filteredLeads = useMemo(() => {
    let base =
      selectedBucket === 'no-followup' ?
        (quickFilter === 'no-followup' ? ratingScopedLeads : noFollowUpScopedLeads) :
        ratingScopedLeads;
    if (selectedTopQueue === 'wa-unread') {
      base = base.filter((l) => whatsappUnreadLeadIds.has(String(l.id)));
    }
    if (selectedAssigneeKey) {
      if (selectedAssigneeKey === '__unassigned__') base = base.filter((l) => !effectiveAssigneeEmail(l));
      else base = base.filter((l) => effectiveAssigneeEmail(l) === selectedAssigneeKey);
    }
    if (quickFilter === 'no-followup') return base;
    if (selectedBucket && selectedBucket !== 'no-followup') {
      base = base.filter((l) => getBucket(l) === selectedBucket);
    }
    if (selectedStatusKey) {
      base = base.filter((l) => {
        const doc = findStatusDoc(l.status);
        const key = doc?.id || normalize(doc?.label || l.status);
        return key === selectedStatusKey;
      });
    }
    return base;
  }, [
    findStatusDoc,
    effectiveAssigneeEmail,
    getBucket,
    noFollowUpScopedLeads,
    normalize,
    quickFilter,
    ratingScopedLeads,
    selectedAssigneeKey,
    selectedBucket,
    selectedStatusKey,
    selectedTopQueue,
    whatsappUnreadLeadIds,
  ]);

  // Debounce the search input for smoother typing.
  useEffect(() => {
    const t = window.setTimeout(() => setSearch(searchRaw.trim()), 200);
    return () => window.clearTimeout(t);
  }, [searchRaw]);

  const searchedLeads = useMemo(() => {
    if (!search) return filteredLeads;
    const q = search.toLowerCase();
    const qDigits = q.replace(/\D/g, '');
    return filteredLeads.filter((l: any) => {
      const name = String(l?.name || '').toLowerCase();
      const phone = String(l?.phone || '').toLowerCase();
      const phoneDigits = `${String(l?.phone || '')} ${String(l?.phone10 || '')}`.replace(/\D/g, '');
      const email = String(l?.email || '').toLowerCase();
      const source = String(l?.source || '').toLowerCase();
      return (
        name.includes(q) ||
        phone.includes(q) ||
        (qDigits ? phoneDigits.includes(qDigits) : false) ||
        email.includes(q) ||
        source.includes(q)
      );
    });
  }, [filteredLeads, search]);

  useEffect(() => {
    let cancelled = false;
    const visible = searchedLeads.slice(0, 100);
    const pending = visible.filter((l: any) => {
      const base = Number(
        (l as any).siteVisitCount ?? (l as any).visitCount ?? (l as any).visits ?? (l as any).visitNos ?? 0
      ) || 0;
      return base <= 0;
    });
    if (!pending.length) return undefined;

    (async () => {
      try {
        const results = await Promise.all(
          pending.map(async (l) => {
            const c = await getCountFromServer(
              query(collection(db, 'leads', String(l.id), 'activities'), where('type', '==', 'visit'))
            );
            return [String(l.id), c.data().count || 0] as const;
          })
        );
        if (cancelled) return;
        setSiteVisitCountByLead((prev) => {
          const next = { ...prev };
          results.forEach(([id, count]) => {
            next[id] = count;
          });
          return next;
        });
      } catch {
        // keep UI stable even if count query fails for some leads
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [db, searchedLeads]);

  // On mobile, default to showing follow-ups for the signed-in user.
  useEffect(() => {
    if (!isMobile || !canSeeOthers) {
      setMobileDefaultAssigneeReady(true);
      return;
    }
    if (!treeAgg.assignees.length) {
      if (!loading) setMobileDefaultAssigneeReady(true);
      return;
    }
    if (didAutoSelectMobileAssignee.current || selectedAssigneeKey !== null) {
      setMobileDefaultAssigneeReady(true);
      return;
    }
    const me = currentUser?.email || '';
    if (!me) {
      setMobileDefaultAssigneeReady(true);
      return;
    }
    const exists = treeAgg.assignees.some((a) => a.key === me);
    if (!exists) {
      setMobileDefaultAssigneeReady(true);
      return;
    }
    setSelectedAssigneeKey(me);
    setSelectedBucket(null);
    setSelectedStatusKey(null);
    didAutoSelectMobileAssignee.current = true;
    setMobileDefaultAssigneeReady(true);
  }, [canSeeOthers, currentUser?.email, isMobile, loading, selectedAssigneeKey, treeAgg.assignees]);

  const detailLead = useMemo(
    () =>
      detailLeadId ? (leads.find((l) => l.id === detailLeadId) || detailLeadFallback || null) : null,
    [detailLeadFallback, detailLeadId, leads]
  );

  const urlLeadId = useMemo(() => searchParams.get('leadId'), [searchParams]);
  // ?log=visit (My Today ✓ Visited / Finish visit) opens the Visit log.
  const urlStartVisitLog = useMemo(() => searchParams.get('log') === 'visit', [searchParams]);

  const setUrlLeadId = useCallback((id: string | null, replace: boolean) => {
    const next = new URLSearchParams(searchParams);
    if (id) next.set('leadId', id);
    else next.delete('leadId');
    if (!id || id !== searchParams.get('leadId')) next.delete('log');
    setSearchParams(next, { replace });
  }, [searchParams, setSearchParams]);

  const runPhoneLookup = useCallback(async () => {
    const digits = String(phoneLookupRaw || '').replace(/\D/g, '');
    const phone10 = digits.length >= 10 ? digits.slice(-10) : '';
    if (phone10.length !== 10) {
      setPhoneLookupError('Enter full 10-digit phone number.');
      return;
    }

    setPhoneLookupBusy(true);
    setPhoneLookupError(null);
    try {
      const snap = await getDocs(
        query(collection(db, 'leads'), where('phone10', '==', phone10), limit(25))
      );
      const matches = snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) } as Lead));
      const accessible = matches.filter((l) => {
        const assignee = effectiveAssigneeEmail(l);
        if (!assignee) return isAdminViewer || isManagerViewer;
        if (visibleAssigneeEmails.mode === 'all') return true;
        return visibleAssigneeEmails.set.has(assignee);
      });
      if (!accessible.length) {
        setPhoneLookupError('No accessible lead found for this number.');
        return;
      }

      const lead = accessible[0];
      setDetailLeadId(lead.id);
      setDetailDrawerOpen(true);
      setDetailLeadFallback(lead);
      setUrlLeadId(lead.id, true);
      setPhoneLookupOpen(false);
      setPhoneLookupRaw('');
    } catch (e: any) {
      setPhoneLookupError(String(e?.message || 'Phone lookup failed.'));
    } finally {
      setPhoneLookupBusy(false);
    }
  }, [db, effectiveAssigneeEmail, isAdminViewer, isManagerViewer, phoneLookupRaw, setUrlLeadId, visibleAssigneeEmails]);

  const openLeadDetails = useCallback((id: string) => {
    setDetailLeadId(id);
    setDetailDrawerOpen(true);
    setUrlLeadId(id, !!detailLeadId);
  }, [detailLeadId, setUrlLeadId]);

  const closeLeadDetails = useCallback(() => {
    setDetailDrawerOpen(false);
    setUrlLeadId(null, true);
  }, [setUrlLeadId]);

  const handleLeadDrawerExited = useCallback(() => {
    setDetailLeadId(null);
    setDetailLeadFallback(null);
  }, []);

  const handleActivityLogged = useCallback((next: Lead) => {
    const rawDate = (next as any).nextFollowUpDate;
    const nextDate = rawDate?.toDate ? rawDate.toDate() : null;
    const isClosed = isClosedLikeStatus(next.status);
    const isDue = !!nextDate && nextDate <= endOfToday;
    const isNoFollowUp = !nextDate;

    setLeads((prev) => {
      const without = prev.filter((lead) => lead.id !== next.id);
      if (isClosed || !isDue) return without;
      return [...without, next].sort((a, b) => {
        const da = a.nextFollowUpDate?.toDate ? a.nextFollowUpDate.toDate().getTime() : 0;
        const db = b.nextFollowUpDate?.toDate ? b.nextFollowUpDate.toDate().getTime() : 0;
        return da - db;
      });
    });

    setNoFollowUpLeads((prev) => {
      const without = prev.filter((lead) => lead.id !== next.id);
      if (isClosed || !isNoFollowUp) return without;
      return [next, ...without];
    });

    setDetailLeadFallback((prev) => (prev?.id === next.id ? ({ ...prev, ...next } as Lead) : prev));
  }, [endOfToday, isClosedLikeStatus]);

  // Sync drawer open/close with URL param so mobile back closes the drawer (instead of leaving the page).
  useEffect(() => {
    if (!urlLeadId) {
      setDetailDrawerOpen(false);
      return;
    }
    if (urlLeadId !== detailLeadId) setDetailLeadId(urlLeadId);
    setDetailDrawerOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlLeadId]);

  // If the lead isn't loaded in the current list, fetch it for the drawer (best-effort).
  useEffect(() => {
    const id = detailLeadId;
    if (!id) return;
    const inList = leads.some((l) => l.id === id);
    if (inList) {
      setDetailLeadFallback(null);
      return;
    }
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'leads', id));
        if (!snap.exists()) return;
        setDetailLeadFallback({ id: snap.id, ...(snap.data() as any) } as Lead);
      } catch (e) {
        console.error('Failed to fetch lead for drawer', e);
      }
    })();
  }, [db, detailLeadId, leads]);

  // Reserve the full drawer width so the list never goes behind the detail panel.
  const detailOffset = detailDrawerOpen ? Math.max(detailWidth, 0) : 0;
  const selectedLeadId = detailLeadId;

  const mobileAssigneeKey = useMemo(() => {
    // If the user cannot see others, treat selection as "Me".
    if (canSeeOthers) return selectedAssigneeKey;
    return currentUser?.email || null;
  }, [canSeeOthers, currentUser?.email, selectedAssigneeKey]);

  const assigneeAggForUi = useMemo(() => {
    if (!treeAgg.assignees.length) return null;
    if (!canSeeOthers) {
      const me = currentUser?.email || '';
      return treeAgg.assignees.find((a) => a.key === me) || null;
    }
    if (mobileAssigneeKey === null) return null; // All
    return treeAgg.assignees.find((a) => a.key === mobileAssigneeKey) || null;
  }, [canSeeOthers, currentUser?.email, mobileAssigneeKey, treeAgg.assignees]);

  const bucketCounts = useMemo(() => {
    if (!canSeeOthers) {
      const a = assigneeAggForUi;
      return {
        today: a?.today.total || 0,
        past: a?.past.total || 0,
        noFollowUp: a?.noFollowUp.total || 0,
      };
    }
    if (mobileAssigneeKey === null) {
      // All assignees
      let t = 0;
      let p = 0;
      let n = 0;
      treeAgg.assignees.forEach((a) => {
        t += a.today.total;
        p += a.past.total;
        n += a.noFollowUp.total;
      });
      return { today: t, past: p, noFollowUp: n };
    }
    const a = assigneeAggForUi;
    return {
      today: a?.today.total || 0,
      past: a?.past.total || 0,
      noFollowUp: a?.noFollowUp.total || 0,
    };
  }, [assigneeAggForUi, canSeeOthers, mobileAssigneeKey, treeAgg.assignees]);

  const statusAggForUi = useMemo(() => {
    if (!selectedBucket) return { total: 0, rows: [] as StatusAgg[] };
    const bucket = selectedBucket;
    if (!canSeeOthers) {
      const a = assigneeAggForUi;
      return bucket === 'past' ?
        (a?.past || { total: 0, rows: [] }) :
        bucket === 'no-followup' ?
          (a?.noFollowUp || { total: 0, rows: [] }) :
          (a?.today || { total: 0, rows: [] });
    }
    if (mobileAssigneeKey === null) {
      // Aggregate across all assignees for the bucket.
      const base: Lead[] = [];
      const source =
        bucket === 'no-followup' ?
          (quickFilter === 'no-followup' ? ratingScopedLeads : noFollowUpScopedLeads) :
          ratingScopedLeads;
      source.forEach((l) => {
        if (getBucket(l) === bucket) base.push(l);
      });
      if (bucket === 'no-followup') return computeAgg(source);
      return computeAgg(base);
    }
    const a = assigneeAggForUi;
    return bucket === 'past' ?
      (a?.past || { total: 0, rows: [] }) :
      bucket === 'no-followup' ?
        (a?.noFollowUp || { total: 0, rows: [] }) :
        (a?.today || { total: 0, rows: [] });
  }, [
    assigneeAggForUi,
    canSeeOthers,
    computeAgg,
    getBucket,
    mobileAssigneeKey,
    noFollowUpScopedLeads,
    quickFilter,
    ratingScopedLeads,
    selectedBucket,
  ]);

  const breadcrumbParts = useMemo(() => {
    const parts: { key: 'assignee' | 'bucket' | 'status' | 'quick'; label: string }[] = [];
    const me = currentUser?.email || '';
    const assigneeLabelText = !canSeeOthers
      ? 'Me'
      : (mobileAssigneeKey === null
          ? 'All'
          : mobileAssigneeKey === '__unassigned__'
            ? 'Unassigned'
            : mobileAssigneeKey === me
              ? 'Me'
              : assigneeLabel(mobileAssigneeKey));
    parts.push({ key: 'assignee', label: assigneeLabelText });
    if (selectedBucket) {
      parts.push({
        key: 'bucket',
        label:
          selectedBucket === 'today' ?
            'Today' :
            selectedBucket === 'past' ?
              'Past' :
              'No Follow-up Date',
      });
    }
    if (selectedStatusKey) {
      const row = statusAggForUi.rows.find((r) => r.key === selectedStatusKey);
      parts.push({ key: 'status', label: row?.label || 'Status' });
    }
    if (selectedTopQueue === 'wa-unread') {
      parts.push({ key: 'quick', label: 'Unread WhatsApp' });
    }
    if (quickFilter) {
      const label =
        quickFilter === 'no-activity'
          ? 'Quick: No Activity'
          : quickFilter === 'no-followup'
            ? 'Quick: No Follow-up Date'
          : quickFilter === 'scheduled-today'
            ? 'Quick: Scheduled Today'
            : quickFilter === 'scheduled-upcoming'
              ? 'Quick: Upcoming'
              : quickFilter === 'overdue-followup'
                ? 'Quick: Overdue'
                : quickFilter === 'unassigned'
                  ? 'Quick: Unassigned'
                  : 'Quick: High Rating';
      parts.push({ key: 'quick', label });
    }
    return parts;
  }, [assigneeLabel, canSeeOthers, currentUser?.email, mobileAssigneeKey, quickFilter, selectedBucket, selectedStatusKey, selectedTopQueue, statusAggForUi.rows]);

  const clearDeepestMobileFilter = useCallback(() => {
    if (selectedStatusKey) setSelectedStatusKey(null);
    else if (selectedTopQueue) setSelectedTopQueue(null);
    else if (quickFilter) setQuickFilter(null);
    else if (ratingFilter !== null) setRatingFilter(null);
    else if (selectedBucket) setSelectedBucket(null);
    else if (canSeeOthers && selectedAssigneeKey !== null) setSelectedAssigneeKey(null);
  }, [canSeeOthers, quickFilter, ratingFilter, selectedAssigneeKey, selectedBucket, selectedStatusKey, selectedTopQueue]);

  useEffect(() => {
    if (selectedTopQueue === 'wa-unread' && whatsappUnreadCount === 0) {
      setSelectedTopQueue(null);
    }
  }, [selectedTopQueue, whatsappUnreadCount]);

  const extraFiltersActive = quickFilter !== null || ratingFilter !== null;
  const extraFiltersPopoverOpen = Boolean(extraFiltersAnchorEl);
  const openExtraFilters = (e: React.MouseEvent<HTMLElement>) => {
    if (isMobile) setExtraFiltersOpenMobile(true);
    else setExtraFiltersAnchorEl(e.currentTarget);
  };
  const closeExtraFilters = () => {
    setExtraFiltersOpenMobile(false);
    setExtraFiltersAnchorEl(null);
  };

  const mobileChipSx = useCallback(
    (selected: boolean) => ({
      height: 28,
      borderRadius: 999,
      fontWeight: selected ? 700 : 600,
      borderColor: selected ? 'primary.main' : 'divider',
      backgroundColor: selected ? 'primary.lighter' : 'transparent',
      '&:hover': {
        borderColor: selected ? 'primary.main' : alpha(theme.palette.primary.main, 0.45),
        backgroundColor: selected ? 'primary.lighter' : alpha(theme.palette.primary.main, 0.05),
      },
    }),
    [theme.palette.primary.main]
  );

  const treeAssigneesForUi = useMemo(() => {
    if (!treeAgg.assignees.length) return [] as AssigneeAgg[];
    if (canSeeOthers) return treeAgg.assignees;
    const me = currentUser?.email || '';
    if (!me) return [];
    return treeAgg.assignees.filter((a) => a.key === me);
  }, [canSeeOthers, currentUser?.email, treeAgg.assignees]);

  const TreeFilterPanel = (
    <Box
      sx={{
        width: treePanelWidth,
        minWidth: 220,
        maxWidth: 420,
        // AppSheet-like: looks like the first column (not a boxed card).
        borderRight: '1px solid',
        borderTop: '1px solid',
        borderColor: 'divider',
        backgroundColor: 'background.paper',
        position: 'sticky',
        top: 0,
        alignSelf: 'stretch',
        height: '100%',
        minHeight: '100vh',
      }}
    >
      <Box sx={{ px: 1.5, py: 1.25, maxHeight: 'calc(100vh - 140px)', overflowY: 'auto' }}>
        <Box
          onClick={() => {
            setSelectedTopQueue(null);
            setSelectedAssigneeKey(null);
            setSelectedBucket(null);
            setSelectedStatusKey(null);
            setTreeOpenAssigneeKey(null);
            setTreeOpenBucket(null);
          }}
          sx={{
            px: 0,
            py: 0.55,
            borderRadius: 1,
            cursor: 'pointer',
            fontWeight: 700,
            backgroundColor: selectedAssigneeKey === null ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: 13 }}>All</Typography>
          <Box
            sx={{
              ml: 1,
              px: 1,
              py: 0.25,
              borderRadius: 1,
              backgroundColor: alpha(theme.palette.common.black, 0.06),
              fontWeight: 700,
              fontSize: 11,
            }}
          >
            {treeAgg.total}
          </Box>
        </Box>

        {whatsappUnreadCount > 0 && (
          <Box
            onClick={() => {
              setSelectedTopQueue((prev) => (prev === 'wa-unread' ? null : 'wa-unread'));
              setSelectedAssigneeKey(null);
              setSelectedBucket(null);
              setSelectedStatusKey(null);
              setTreeOpenAssigneeKey(null);
              setTreeOpenBucket(null);
            }}
            sx={{
              mt: 0.65,
              px: 0,
              py: 0.55,
              borderRadius: 1,
              cursor: 'pointer',
              fontWeight: 700,
              backgroundColor:
                selectedTopQueue === 'wa-unread' ?
                  alpha(theme.palette.success.main, 0.12) :
                  'transparent',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              color: selectedTopQueue === 'wa-unread' ? 'success.dark' : 'text.primary',
            }}
          >
            <Stack direction="row" spacing={0.9} alignItems="center">
              <WhatsAppIcon sx={{ fontSize: 16, color: '#25D366' }} />
              <Typography sx={{ fontWeight: 700, fontSize: 13 }}>
                Unread WhatsApp
              </Typography>
            </Stack>
            <Box
              sx={{
                ml: 1,
                px: 1,
                py: 0.25,
                borderRadius: 1,
                backgroundColor: alpha('#25D366', 0.14),
                color: 'success.dark',
                fontWeight: 700,
                fontSize: 11,
              }}
            >
              {whatsappUnreadCount}
            </Box>
          </Box>
        )}

        <Box sx={{ mt: 0.75 }}>
          {treeAssigneesForUi.map((a) => {
            const me = currentUser?.email || '';
            const label = a.key === me ? 'Me' : a.label;
            const isOpen = treeOpenAssigneeKey === a.key;
            const isSelected = selectedAssigneeKey === a.key;
            return (
              <Box key={a.key} sx={{ mt: 0.5 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    cursor: 'pointer',
                    px: 1,
                    py: 0,
                    borderRadius: 2,
                    '&:hover': { backgroundColor: alpha(theme.palette.common.black, 0.03) },
                  }}
                  onClick={() => {
                    const next = isSelected ? null : a.key;
                    setSelectedTopQueue(null);
                    setSelectedAssigneeKey(next);
                    setSelectedBucket(null);
                    setSelectedStatusKey(null);
                    setTreeOpenAssigneeKey(next ? a.key : null);
                    setTreeOpenBucket(null);
                  }}
                >
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setTreeOpenAssigneeKey((v) => (v === a.key ? null : a.key));
                      setTreeOpenBucket(null);
                    }}
                    sx={{ mr: 0.25 }}
                    aria-label={isOpen ? 'Collapse' : 'Expand'}
                  >
                    <ExpandMoreIcon
                      fontSize="small"
                      sx={{ transform: isOpen ? 'rotate(90deg)' : 'none', transition: 'transform 120ms ease' }}
                    />
                  </IconButton>
                  <Typography sx={{ fontWeight: 700, fontSize: 13, color: 'primary.main' }}>{label}</Typography>
                  <Box
                    sx={{
                      ml: 0.5,
                      px: 1,
                      py: 0.25,
                      borderRadius: 1,
                      backgroundColor: alpha(theme.palette.common.black, 0.06),
                      fontWeight: 700,
                      fontSize: 11,
                    }}
                  >
                    {a.total}
                  </Box>
                </Box>

                <Collapse in={isOpen} timeout="auto" unmountOnExit>
                  <Box sx={{ pl: 4, pt: 0.5 }}>
                    {(['today', 'past', 'no-followup'] as const).map((bucket) => {
                      const bucketAgg =
                        bucket === 'today' ?
                          a.today :
                          bucket === 'past' ?
                            a.past :
                            a.noFollowUp;
                      const bucketLabel =
                        bucket === 'today' ? 'Today' : bucket === 'past' ? 'Past' : 'No Follow-up Date';
                      const bucketOpen = treeOpenBucket === bucket;
                      const bucketSelected = selectedAssigneeKey === a.key && selectedBucket === bucket;
                      return (
                        <Box key={bucket} sx={{ mt: 0.35 }}>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              cursor: 'pointer',
                              px: 1,
                              py: 0,
                              borderRadius: 2,
                              '&:hover': { backgroundColor: alpha(theme.palette.common.black, 0.03) },
                            }}
                            onClick={() => {
                              const nextBucket = bucketSelected ? null : bucket;
                              setSelectedTopQueue(null);
                              setSelectedAssigneeKey(a.key);
                              setQuickFilter(null);
                              setSelectedBucket(nextBucket);
                              setSelectedStatusKey(null);
                              setTreeOpenBucket(nextBucket ? bucket : null);
                            }}
                          >
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                setTreeOpenBucket((v) => (v === bucket ? null : bucket));
                              }}
                              aria-label={bucketOpen ? 'Collapse' : 'Expand'}
                            >
                              <ExpandMoreIcon
                                fontSize="small"
                                sx={{
                                  transform: bucketOpen ? 'rotate(90deg)' : 'none',
                                  transition: 'transform 120ms ease',
                                }}
                              />
                            </IconButton>
                            <Typography sx={{ fontWeight: 700, fontSize: 13 }}>{bucketLabel}</Typography>
                            <Box
                              sx={{
                                ml: 0.5,
                                px: 1,
                                py: 0.25,
                                borderRadius: 1,
                                backgroundColor: alpha(theme.palette.common.black, 0.06),
                                fontWeight: 700,
                                fontSize: 11,
                              }}
                            >
                              {bucketAgg.total}
                            </Box>
                          </Box>

                          <Collapse in={bucketOpen} timeout="auto" unmountOnExit>
                            <Box sx={{ pl: 4, pt: 0.5 }}>
                              {bucketAgg.rows.map((r) => {
                                const selected =
                                  selectedAssigneeKey === a.key &&
                                  selectedBucket === bucket &&
                                  selectedStatusKey === r.key;
                                return (
                                  <Box
                                    key={r.key}
                                    onClick={() => {
                                      setSelectedTopQueue(null);
                                      setSelectedAssigneeKey(a.key);
                                      setSelectedBucket(bucket);
                                      setSelectedStatusKey(selected ? null : r.key);
                                    }}
                                    sx={{
                                      mt: 0.25,
                                      px: 1.25,
                                      py: 0.5,
                                      borderRadius: 999,
                                      cursor: 'pointer',
                                      border: '2px solid',
                                      borderColor: selected ? 'primary.main' : 'transparent',
                                      backgroundColor: selected
                                        ? alpha(theme.palette.primary.main, 0.08)
                                        : 'transparent',
                                      display: 'flex',
                                      alignItems: 'center',
                                      
                                      '&:hover': {
                                        backgroundColor: selected
                                          ? alpha(theme.palette.primary.main, 0.1)
                                          : alpha(theme.palette.common.black, 0.03),
                                      },
                                    }}
                                    >
                                    <Typography
                                      sx={{
                                        fontWeight: selected ? 700 : 600,
                                        fontSize: 13,
                                        color: selected ? 'primary.main' : 'text.primary',
                                      }}
                                    >
                                      {r.label}
                                    </Typography>
                                    <Box
                                      sx={{
                                        ml: 1,
                                        px: 1,
                                        py: 0.25,
                                        borderRadius: 1,
                                        backgroundColor: alpha(theme.palette.common.black, 0.06),
                                        fontWeight: 700,
                                        fontSize: 11,
                                      }}
                                    >
                                      {r.count}
                                    </Box>
                                  </Box>
                                );
                              })}
                            </Box>
                          </Collapse>
                        </Box>
                      );
                    })}
                  </Box>
                </Collapse>
              </Box>
            );
          })}
        </Box>
      </Box>

      <Box
        onMouseDown={(e) => {
          treeResizeRef.current = { startX: e.clientX, startW: treePanelWidth };
          setTreeResizing(true);
        }}
        sx={{
          position: 'absolute',
          top: 0,
          right: -6,
          width: 12,
          height: '100%',
          cursor: 'col-resize',
        }}
      />
    </Box>
  );

  const ExtraFiltersContent = (
    <Box sx={{ p: 2, width: isMobile ? '100%' : 360 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
          Filters
        </Typography>
        <IconButton size="small" onClick={closeExtraFilters} aria-label="close filters">
          <CloseOutlinedIcon fontSize="small" />
        </IconButton>
      </Stack>

      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.75, fontWeight: 800 }}>
        Quick Filters
      </Typography>
      <Stack
        direction="row"
        gap={1}
        sx={{
          overflowX: 'auto',
          pb: 0.5,
          '&::-webkit-scrollbar': { display: 'none' },
          msOverflowStyle: 'none',
          scrollbarWidth: 'none',
        }}
      >
        <Chip
          clickable
          label="All"
          variant={quickFilter === null ? 'filled' : 'outlined'}
          sx={mobileChipSx(quickFilter === null)}
          onClick={() => setQuickFilter(null)}
        />
        <Chip
          clickable
          label="No Activity"
          variant={quickFilter === 'no-activity' ? 'filled' : 'outlined'}
          sx={mobileChipSx(quickFilter === 'no-activity')}
          onClick={() => setQuickFilter('no-activity')}
        />
        <Chip
          clickable
          label="No Follow-up Date"
          variant={quickFilter === 'no-followup' ? 'filled' : 'outlined'}
          sx={mobileChipSx(quickFilter === 'no-followup')}
          onClick={() => {
            setQuickFilter('no-followup');
            setSelectedBucket(null);
            setSelectedStatusKey(null);
          }}
        />
        <Chip
          clickable
          label="Scheduled Today"
          variant={quickFilter === 'scheduled-today' ? 'filled' : 'outlined'}
          sx={mobileChipSx(quickFilter === 'scheduled-today')}
          onClick={() => setQuickFilter('scheduled-today')}
        />
        <Chip
          clickable
          label="Upcoming"
          variant={quickFilter === 'scheduled-upcoming' ? 'filled' : 'outlined'}
          sx={mobileChipSx(quickFilter === 'scheduled-upcoming')}
          onClick={() => setQuickFilter('scheduled-upcoming')}
        />
        <Chip
          clickable
          label="Overdue"
          variant={quickFilter === 'overdue-followup' ? 'filled' : 'outlined'}
          sx={mobileChipSx(quickFilter === 'overdue-followup')}
          onClick={() => setQuickFilter('overdue-followup')}
        />
        {canSeeOthers && (
          <Chip
            clickable
            label="Unassigned"
            variant={quickFilter === 'unassigned' ? 'filled' : 'outlined'}
            sx={mobileChipSx(quickFilter === 'unassigned')}
            onClick={() => setQuickFilter('unassigned')}
          />
        )}
        <Chip
          clickable
          label="4-5★"
          variant={quickFilter === 'high-rating' ? 'filled' : 'outlined'}
          sx={mobileChipSx(quickFilter === 'high-rating')}
          onClick={() => setQuickFilter('high-rating')}
        />
      </Stack>

      <Divider sx={{ my: 1.5 }} />

      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.75, fontWeight: 800 }}>
        Star Rating
      </Typography>
      <Stack
        direction="row"
        gap={1}
        sx={{
          overflowX: 'auto',
          pb: 0.5,
          '&::-webkit-scrollbar': { display: 'none' },
          msOverflowStyle: 'none',
          scrollbarWidth: 'none',
        }}
      >
        <Chip
          clickable
          label="Any"
          variant={ratingFilter === null ? 'filled' : 'outlined'}
          sx={mobileChipSx(ratingFilter === null)}
          onClick={() => setRatingFilter(null)}
        />
        {[5, 4, 3, 2, 1].map((n) => (
          <Chip
            key={n}
            clickable
            label={`${n}★`}
            variant={ratingFilter === n ? 'filled' : 'outlined'}
            sx={mobileChipSx(ratingFilter === n)}
            onClick={() => setRatingFilter(n)}
          />
        ))}
        <Chip
          clickable
          label="Unrated"
          variant={ratingFilter === 'unrated' ? 'filled' : 'outlined'}
          sx={mobileChipSx(ratingFilter === 'unrated')}
          onClick={() => setRatingFilter('unrated')}
        />
      </Stack>
    </Box>
  );

  const LeadsList = (
    <Paper
      elevation={0}
      sx={{
        p: leadListView === 'sheet' ? 0 : 1,
        width: '100%',
        flex: 1,
        boxShadow: 'none',
        border: 'none',
        backgroundColor: 'transparent',
      }}
    >
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <Stack spacing={leadListView === 'sheet' ? 0 : 1}>
        {!mobileDefaultAssigneeReady && isMobile ? (
          <Alert severity="info">Loading follow-ups...</Alert>
        ) : null}
        {searchedLeads.length === 0 && !loading && mobileDefaultAssigneeReady && (
          <Alert severity="info">No leads in this view.</Alert>
        )}
        {leadListView === 'sheet' && searchedLeads.length > 0 && <Divider sx={{ borderColor: 'divider' }} />}
        {(mobileDefaultAssigneeReady ? searchedLeads : []).map((l) => {
          const sd = findStatusDoc(l.status);
          const statusLabel = sd?.label || l.status;
          const badge = apptIndicator(l);
          const p10 = String((l as any).phone10 || (l as any).phone || '').replace(/\D/g, '').slice(-10);
          return (
            <LeadCompactCard
              key={l.id}
              lead={{ ...(l as any), visitCount: siteVisitCountByLead[l.id] ?? (l as any).visitCount }}
              selected={selectedLeadId === l.id}
              onClick={() => openLeadDetails(l.id)}
              statusLabel={statusLabel}
              assignedLabel={
                effectiveAssigneeEmail(l)
                  ? assigneeLabel(effectiveAssigneeEmail(l)) || '—'
                  : l.assignedToName
                    ? String(l.assignedToName)
                    : 'Unassigned'
              }
              badge={badge}
              accentColor={statusColor(l.status)}
              view={leadListView}
              waUnreadCount={p10 ? (waUnreadByPhone.get(p10) ?? 0) : 0}
            />
          );
        })}
      </Stack>
    </Paper>
  );

  return (
    <>
      <Box sx={{ pr: { xs: 0, sm: `${detailOffset}px` }, transition: 'padding-right 0.2s ease' }}>
        <Stack
          direction="row"
          alignItems="center"
          sx={{
            mb: 2,
            gap: 1,
            flexWrap: { xs: 'wrap', sm: 'nowrap' },
            mt: { xs: 1.5, sm: 1.5, md: 2 },
            mx: { xs: 1, sm: 1.5, md: 2 },
            minWidth: 0,
          }}
        >
          <Typography
            variant="h5"
            sx={{ whiteSpace: 'nowrap', flexShrink: 0, fontSize: { xs: '1.15rem', sm: '1.5rem' } }}
          >
            Follow-ups
          </Typography>
          <TextField
            size="small"
            placeholder="Search leads..."
            value={searchRaw}
            onChange={(e) => setSearchRaw(e.target.value)}
            sx={{
              display: { xs: 'none', sm: 'block' },
              width: { sm: 280, md: 320 },
              minWidth: { xs: 0, sm: 220 },
              flex: { sm: '0 0 auto' },
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'grey.50',
                borderRadius: 2,
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlinedIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: searchRaw ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setSearchRaw('')}
                    edge="end"
                    aria-label="clear search"
                  >
                    <CloseOutlinedIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ whiteSpace: 'nowrap', display: { xs: 'none', sm: 'block' } }}
          >
            Showing {searchedLeads.length} of {filteredLeads.length}
          </Typography>
          <Box sx={{ flex: 1, display: { xs: 'none', sm: 'block' } }} />
          <Stack
            direction="row"
            gap={1}
            alignItems="center"
            sx={{ flexShrink: 0, ml: { xs: 'auto', sm: 0 } }}
          >
            {isMobile && (
              <Tooltip title={mobileSearchOpen ? 'Close search' : 'Search'}>
                <IconButton
                  size="small"
                  aria-label={mobileSearchOpen ? 'Close search' : 'Search'}
                  onClick={() => {
                    setMobileSearchOpen((v) => !v);
                    if (mobileSearchOpen) setSearchRaw('');
                  }}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    color: 'primary.main',
                    backgroundColor: mobileSearchOpen ? 'primary.lighter' : 'transparent',
                  }}
                >
                  {mobileSearchOpen ? (
                    <CloseOutlinedIcon fontSize="small" />
                  ) : (
                    <SearchOutlinedIcon fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Phone Lookup">
              <IconButton
                size="small"
                aria-label="Phone Lookup"
                onClick={() => {
                  setPhoneLookupOpen(true);
                  setPhoneLookupError(null);
                }}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  color: 'primary.main',
                  backgroundColor: phoneLookupOpen ? 'primary.lighter' : 'transparent',
                }}
              >
                <PhoneInTalkOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <IconButton
              size="small"
              aria-label="New lead"
              onClick={() => setDrawerOpen(true)}
              disabled={!canAction('leads', 'addLead')}
              sx={{
                border: '1px solid',
                borderColor: 'primary.main',
                borderRadius: 2,
                color: 'primary.contrastText',
                backgroundColor: 'primary.main',
                '&:hover': { backgroundColor: 'primary.dark' },
              }}
            >
              <AddIcon fontSize="small" />
            </IconButton>
            {isDesktop && (
              <Tooltip title={filterUiMode === 'tree' ? 'Tree filters' : 'Pill filters'}>
                <IconButton
                  size="small"
                  aria-label={filterUiMode === 'tree' ? 'Tree filters' : 'Pill filters'}
                  onClick={() => setFilterUiMode((v) => (v === 'tree' ? 'pills' : 'tree'))}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    color: 'primary.main',
                    backgroundColor: filterUiMode === 'tree' ? 'primary.lighter' : 'transparent',
                  }}
                >
                  <AccountTreeOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {isAdmin && (
              <Tooltip title={leadListView === 'card' ? 'Switch to list view' : 'Switch to card view'}>
                <IconButton
                  size="small"
                  aria-label={leadListView === 'card' ? 'Switch to list view' : 'Switch to card view'}
                  onClick={() => setLeadListView((v) => (v === 'card' ? 'sheet' : 'card'))}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    color: 'primary.main',
                    backgroundColor: leadListView === 'sheet' ? 'primary.lighter' : 'transparent',
                  }}
                >
                  {leadListView === 'card' ? (
                    <ViewAgendaOutlinedIcon fontSize="small" />
                  ) : (
                    <ViewModuleOutlinedIcon fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Stack>
        {isMobile && mobileSearchOpen && (
          <Box
            sx={{
              mx: 0,
              mb: 1.5,
              px: { xs: 1.25, sm: 1.5 },
              py: 1.25,
              width: '100%',
              bgcolor: 'background.paper',
              borderTop: '1px solid',
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <TextField
              size="small"
              fullWidth
              placeholder="Search leads..."
              value={searchRaw}
              onChange={(e) => setSearchRaw(e.target.value)}
              autoFocus
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchOutlinedIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: searchRaw ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setSearchRaw('')}
                      edge="end"
                      aria-label="clear search"
                    >
                      <CloseOutlinedIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />
          </Box>
        )}
        <Dialog
          open={phoneLookupOpen}
          onClose={() => setPhoneLookupOpen(false)}
          fullWidth
          maxWidth="xs"
        >
          <DialogTitle>Phone Lookup</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              size="small"
              fullWidth
              placeholder="Enter full phone number"
              value={phoneLookupRaw}
              onChange={(e) => setPhoneLookupRaw(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  runPhoneLookup();
                }
              }}
              sx={{ mt: 0.5 }}
            />
            {phoneLookupError && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                {phoneLookupError}
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setPhoneLookupOpen(false);
                setPhoneLookupError(null);
              }}
            >
              Cancel
            </Button>
            <Button variant="contained" onClick={runPhoneLookup} disabled={phoneLookupBusy}>
              {phoneLookupBusy ? 'Searching…' : 'Find Lead'}
            </Button>
          </DialogActions>
        </Dialog>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          {!isDesktop || filterUiMode === 'pills' ? (
            <Paper
              elevation={0}
              sx={{
                p: leadListView === 'sheet' ? 0 : 1,
                ...(leadListView === 'sheet'
                  ? {
                      width: '100%',
                      border: 'none',
                      borderRadius: 0,
                      backgroundImage: 'none',
                      backgroundColor: 'background.paper',
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      pb: 0.75,
                    }
                  : {
                      mx: { xs: 1, sm: 1, md: 0 },
                      width: '100%',
                      border: '1px solid',
                      borderColor: alpha(theme.palette.primary.main, 0.18),
                      borderRadius: 2,
                      backgroundImage: `linear-gradient(180deg, ${alpha(
                        theme.palette.primary.main,
                        0.05
                      )} 0%, ${alpha('#ffffff', 0)} 60%)`,
                    }),
                boxShadow: 'none',
              }}
            >
              <Box
                role="button"
                tabIndex={0}
                onClick={() => setMobileInlineOpen((v) => !v)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') setMobileInlineOpen((v) => !v);
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  flexWrap: 'wrap',
                  mb: 0.75,
                  px: leadListView === 'sheet' ? { xs: 1.5, sm: 2.5 } : 0.5,
                  py: 0.25,
                  borderRadius: 2,
                  cursor: 'pointer',
                  backgroundColor: alpha(theme.palette.common.black, 0.02),
                  '&:active': { backgroundColor: alpha(theme.palette.common.black, 0.04) },
                }}
              >
                {breadcrumbParts.map((p, idx) => (
                  <Box
                    key={`${p.key}:${p.label}`}
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      cursor: 'pointer',
                      color: idx === breadcrumbParts.length - 1 ? 'text.primary' : 'text.secondary',
                      fontSize: 13,
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (p.key === 'assignee') {
                        setSelectedBucket(null);
                        setSelectedStatusKey(null);
                      } else if (p.key === 'bucket') {
                        setSelectedStatusKey(null);
                      } else if (p.key === 'quick') {
                        setQuickFilter(null);
                      }
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: 13,
                        fontWeight: idx === breadcrumbParts.length - 1 ? 700 : 600,
                      }}
                    >
                      {p.label}
                    </Typography>
                    {idx < breadcrumbParts.length - 1 && (
                      <Typography variant="body2" sx={{ mx: 0.75, fontSize: 13, color: 'text.disabled' }}>
                        {'>'}
                      </Typography>
                    )}
                  </Box>
                ))}
                <Box sx={{ flex: 1 }} />
                <IconButton
                  size="small"
                  aria-label={mobileInlineOpen ? 'collapse filters' : 'expand filters'}
                  onClick={(e) => {
                    e.stopPropagation();
                    setMobileInlineOpen((v) => !v);
                  }}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    backgroundColor: alpha(theme.palette.common.black, 0.02),
                  }}
                >
                  {mobileInlineOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                </IconButton>
                <Badge
                  variant="dot"
                  overlap="circular"
                  color="primary"
                  invisible={!extraFiltersActive}
                  sx={{
                    '& .MuiBadge-badge': {
                      top: 6,
                      right: 6,
                      boxShadow: '0 0 0 2px #fff',
                    },
                  }}
                >
                  <IconButton
                    size="small"
                    aria-label="extra filters"
                    onClick={(e) => {
                      e.stopPropagation();
                      openExtraFilters(e);
                    }}
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                      backgroundColor: alpha(theme.palette.common.black, 0.02),
                    }}
                  >
                    <TuneOutlinedIcon fontSize="small" />
                  </IconButton>
                </Badge>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    clearDeepestMobileFilter();
                  }}
                  aria-label="clear filter"
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    backgroundColor: alpha(theme.palette.common.black, 0.02),
                  }}
                >
                  <CloseOutlinedIcon fontSize="small" />
                </IconButton>
              </Box>

              {mobileInlineOpen && !mobileDefaultAssigneeReady && isMobile && (
                <Box
                  sx={{
                    px: leadListView === 'sheet' ? { xs: 1.5, sm: 2.5 } : 0,
                    py: 0.75,
                    color: 'text.secondary',
                    fontSize: 13,
                    fontWeight: 600,
                  }}
                >
                  Loading filters...
                </Box>
              )}

              {mobileInlineOpen && mobileDefaultAssigneeReady && (
                <>
                  {canSeeOthers && (
                    <Stack
                      direction="row"
                      gap={1}
                      sx={{
                        px: leadListView === 'sheet' ? { xs: 1.5, sm: 2.5 } : 0,
                        overflowX: 'auto',
                        pb: 0.5,
                        '&::-webkit-scrollbar': { display: 'none' },
                        msOverflowStyle: 'none',
                        scrollbarWidth: 'none',
                      }}
                    >
                      <Chip
                        clickable
                        label={`All (${treeAgg.total})`}
                        variant={selectedAssigneeKey === null ? 'filled' : 'outlined'}
                        sx={mobileChipSx(selectedAssigneeKey === null)}
                        onClick={() => {
                          setSelectedTopQueue(null);
                          setSelectedAssigneeKey(null);
                          setSelectedBucket(null);
                          setSelectedStatusKey(null);
                        }}
                      />
                      {whatsappUnreadCount > 0 && (
                        <Chip
                          clickable
                          icon={<WhatsAppIcon sx={{ fontSize: '16px !important', color: '#25D366 !important' }} />}
                          label={`Unread WhatsApp (${whatsappUnreadCount})`}
                          variant={selectedTopQueue === 'wa-unread' ? 'filled' : 'outlined'}
                          sx={mobileChipSx(selectedTopQueue === 'wa-unread')}
                          onClick={() => {
                            setSelectedTopQueue((prev) => (prev === 'wa-unread' ? null : 'wa-unread'));
                            setSelectedAssigneeKey(null);
                            setSelectedBucket(null);
                            setSelectedStatusKey(null);
                          }}
                        />
                      )}
                      {treeAgg.assignees
                        .filter((a) => a.key !== '__unassigned__')
                        .map((a) => {
                          const me = currentUser?.email || '';
                          const label = a.key === me ? 'Me' : a.label;
                          return (
                            <Chip
                              key={a.key}
                              clickable
                              label={`${label} (${a.total})`}
                              variant={selectedAssigneeKey === a.key ? 'filled' : 'outlined'}
                              sx={mobileChipSx(selectedAssigneeKey === a.key)}
                          onClick={() => {
                            setSelectedTopQueue(null);
                            setSelectedAssigneeKey(a.key);
                            setSelectedBucket(null);
                            setSelectedStatusKey(null);
                              }}
                            />
                          );
                        })}
                      {treeAgg.assignees.some((a) => a.key === '__unassigned__') && (
                        <Chip
                          clickable
                          label={`Unassigned (${treeAgg.assignees.find((a) => a.key === '__unassigned__')?.total || 0})`}
                          variant={selectedAssigneeKey === '__unassigned__' ? 'filled' : 'outlined'}
                          sx={mobileChipSx(selectedAssigneeKey === '__unassigned__')}
                          onClick={() => {
                            setSelectedTopQueue(null);
                            setSelectedAssigneeKey('__unassigned__');
                            setSelectedBucket(null);
                            setSelectedStatusKey(null);
                          }}
                        />
                      )}
                    </Stack>
                  )}

                  <Stack
                    direction="row"
                    gap={1}
                    sx={{
                      mt: 1,
                      px: leadListView === 'sheet' ? { xs: 1.5, sm: 2.5 } : 0,
                      overflowX: 'auto',
                      pb: 0.5,
                      '&::-webkit-scrollbar': { display: 'none' },
                    }}
                  >
                    <Chip
                      clickable
                      label={`Today (${bucketCounts.today})`}
                      variant={selectedBucket === 'today' ? 'filled' : 'outlined'}
                      sx={mobileChipSx(selectedBucket === 'today')}
                      onClick={() => {
                        setQuickFilter(null);
                        setSelectedBucket('today');
                        setSelectedStatusKey(null);
                      }}
                    />
                    <Chip
                      clickable
                      label={`Past (${bucketCounts.past})`}
                      variant={selectedBucket === 'past' ? 'filled' : 'outlined'}
                      sx={mobileChipSx(selectedBucket === 'past')}
                      onClick={() => {
                        setQuickFilter(null);
                        setSelectedBucket('past');
                        setSelectedStatusKey(null);
                      }}
                    />
                    <Chip
                      clickable
                      label={`No Follow-up (${bucketCounts.noFollowUp})`}
                      variant={selectedBucket === 'no-followup' ? 'filled' : 'outlined'}
                      sx={mobileChipSx(selectedBucket === 'no-followup')}
                      onClick={() => {
                        setQuickFilter(null);
                        setSelectedBucket('no-followup');
                        setSelectedStatusKey(null);
                      }}
                    />
                  </Stack>

                  {selectedBucket && (
                    <Stack
                      direction="row"
                      gap={1}
                      sx={{
                        mt: 1,
                        px: leadListView === 'sheet' ? { xs: 1.5, sm: 2.5 } : 0,
                        overflowX: 'auto',
                        pb: 0.5,
                        '&::-webkit-scrollbar': { display: 'none' },
                      }}
                    >
                      {statusAggForUi.rows.map((r) => (
                        <Chip
                          key={r.key}
                          clickable
                          label={`${r.label} (${r.count})`}
                          variant={selectedStatusKey === r.key ? 'filled' : 'outlined'}
                          sx={mobileChipSx(selectedStatusKey === r.key)}
                          onClick={() => setSelectedStatusKey(r.key)}
                        />
                      ))}
                    </Stack>
                  )}
                </>
              )}
            </Paper>
          ) : null}

          <Popover
            open={extraFiltersPopoverOpen}
            anchorEl={extraFiltersAnchorEl}
            onClose={closeExtraFilters}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{
              sx: {
                borderRadius: 3,
                overflow: 'hidden',
                boxShadow: '0 18px 48px rgba(15, 23, 42, 0.18)',
              },
            }}
          >
            {ExtraFiltersContent}
          </Popover>

          <Drawer
            anchor="bottom"
            open={extraFiltersOpenMobile}
            onClose={closeExtraFilters}
            PaperProps={{
              sx: {
                borderTopLeftRadius: 16,
                borderTopRightRadius: 16,
                bottom: 0,
                marginBottom: 0,
              },
            }}
            sx={{
              '& .MuiDrawer-paperAnchorBottom': {
                bottom: '0 !important',
                marginBottom: '0 !important',
              },
            }}
          >
            {ExtraFiltersContent}
          </Drawer>

          {isDesktop && filterUiMode === 'tree' ? (
            <Box sx={{ display: 'flex', gap: 0, alignItems: 'stretch' }}>
              {TreeFilterPanel}
              <Box sx={{ flex: 1, minWidth: 0 }}>{LeadsList}</Box>
            </Box>
          ) : (
            LeadsList
          )}
        </Box>
      </Box>

      <AddLeadDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} onCreated={() => {}} />

      <LeadDetailDrawer
        open={detailDrawerOpen}
        lead={detailLead}
        startVisitLog={urlStartVisitLog && !!detailLead && detailLead.id === urlLeadId}
        onClose={closeLeadDetails}
        onExited={handleLeadDrawerExited}
        onUpdated={(next) => {
          setLeads((prev: any[]) => prev.map((l) => (l.id === next.id ? ({ ...l, ...next } as any) : l)));
          setNoFollowUpLeads((prev: any[]) => prev.map((l) => (l.id === next.id ? ({ ...l, ...next } as any) : l)));
          setDetailLeadFallback((prev) => (prev?.id === next.id ? ({ ...prev, ...next } as any) : prev));
        }}
        onActivityLogged={handleActivityLogged}
        onDeleted={(leadId) => {
          setLeads((prev) => prev.filter((l) => l.id !== leadId));
          if (detailLeadId === leadId) closeLeadDetails();
        }}
        onWidthChange={(w) => {
          const minList = 340;
          const maxDrawer = 700;
          const minDrawer = 320;
          const viewport = typeof window !== 'undefined' ? window.innerWidth : 1200;
          const maxAllowed = Math.max(minDrawer, Math.min(maxDrawer, viewport - minList));
          const clamped = Math.min(maxAllowed, Math.max(minDrawer, w || minDrawer));
          setDetailWidth(clamped);
        }}
        mobileFullScreen={isMobile}
      />
    </>
  );
}
